import { Radio, Stack, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { EventHandlingResult, PriceControlAction } from '@retail/price-control/types';
import { Paper } from '@shared/components';
import { EventAction } from './ItemAction';

interface Props {
  action: PriceControlAction;
  newValues: EventHandlingResult;
  oldSalesPrice?: number;
  oldFactorAndDG?: { factor: number; coverageDegree: number };
  isChecked: boolean;
  disabled: boolean;
  includeVat: boolean;
  onCheck: () => void;
}

export function PriceControlItemAction({
  action,
  newValues,
  oldSalesPrice,
  oldFactorAndDG,
  isChecked,
  disabled,
  includeVat,
  onCheck,
}: Props) {
  const t = useAppTFunction();
  const newSalesPrice = includeVat ? newValues.newSalesPriceIncVat : newValues.newSalesPriceExVat;
  return (
    <Paper
      sx={{
        p: 1.8,
        display: 'grid',
        gap: 4,
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'flex-start',
      }}
      variant="outlined"
    >
      <Radio disabled={disabled} checked={isChecked} onClick={onCheck} sx={{ px: 0, py: 0.3 }} />
      <EventAction action={action} />

      <Stack alignItems="flex-end" gap={0.5}>
        <Stack flexDirection="column" alignItems="center">
          <Typography variant="body1">
            {t('products.prices.price', {
              price: newSalesPrice ? newSalesPrice.toFixed(2) : '-',
            })}
          </Typography>
          {oldSalesPrice && (
            <Typography variant="body2" lineHeight={0.7} sx={{ textDecoration: 'line-through' }}>
              {t('products.prices.price', { price: oldSalesPrice ?? '-' })}
            </Typography>
          )}
        </Stack>

        {oldSalesPrice && newSalesPrice && (
          <Typography
            variant="body3"
            color={newSalesPrice - oldSalesPrice > 0 ? 'error' : 'success'}
          >
            {`${newSalesPrice - oldSalesPrice > 0 ? '+' : ''} ${t('products.prices.price', {
              price: (newSalesPrice - oldSalesPrice)?.toFixed(2),
            })}`}
          </Typography>
        )}

        <Stack flexDirection="column" alignItems="center">
          <Typography variant="body3">{`${
            newValues.newCoverageDegree ? newValues.newCoverageDegree.toFixed(3) : '-'
          } % (${
            newValues.newSalesFactor ? newValues.newSalesFactor.toFixed(3) : '-'
          })`}</Typography>

          {oldFactorAndDG && (newValues.newCoverageDegree || newValues.newSalesFactor) && (
            <Typography
              sx={{ textDecoration: 'line-through' }}
              lineHeight={0.7}
              variant="body3"
            >{`${oldFactorAndDG.coverageDegree ?? '-'} % (${
              oldFactorAndDG.factor ?? '-'
            })`}</Typography>
          )}
        </Stack>
        {oldFactorAndDG && newValues.newSalesFactor && (
          <Typography
            pt={0.4}
            color={newValues.newSalesFactor < oldFactorAndDG.factor ? 'error' : 'success'}
            lineHeight={0.7}
            variant="body3"
          >{`${newValues.newSalesFactor < oldFactorAndDG.factor ? '' : '+'} ${(
            oldFactorAndDG.coverageDegree - newValues.newCoverageDegree
          )?.toFixed(2)} % ( ${newValues.newSalesFactor < oldFactorAndDG.factor ? '-' : '+'} ${(
            oldFactorAndDG.factor - newValues.newSalesFactor
          )?.toFixed(2)})`}</Typography>
        )}
      </Stack>
    </Paper>
  );
}
